// The Mirabilis run's own analysis, as the report's Mirabilis view shows it. The forecast
// server attaches it to `report.analysis`; `report.summaries` carries the same key
// uncertainties on reports stored before that block existed.
import { fmtPct } from "./formatPct.js";
import { bucketLabel, intervalDisplayLabel, leadingRow } from "./multiway.js";

// The forecaster's summary opens with a submission receipt ("Report accepted — forecast
// submitted: …"); the reader wants what follows it.
const SUBMISSION_RECEIPT =
  /^\s*(?:report accepted\s*[—-]\s*)?forecast submitted(?: and accepted)?:\s*/i;
// A Quick run's summary is only that receipt ("Report accepted — forecast p=0.06 with five
// trigger contracts submitted."), which says nothing the report doesn't.
const RECEIPT_ONLY = /^report accepted\s*[—-]\s*forecast\s+p\s*=\s*[\d.]+[^.]*\.?$/i;

const strings = (v) =>
  Array.isArray(v) ? v.filter((s) => typeof s === "string" && s.trim()).map((s) => s.trim()) : [];
const text = (v) => (typeof v === "string" && v.trim() ? v.trim() : "");
const number = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

// An uncertainty about the forecasting itself (its cards, tools, arithmetic, starting point)
// rather than the world. Only the fallback for a report written before the server returned
// `content_uncertainties`, which it rewrites to be about the question.
const ABOUT_THE_FORECASTING =
  /\b(evidence cards?|card dates|prefetch|run_python|calculation tool|tool (was |is )?used|by hand|forecaster|harness (?:run|trace)|root researcher|synthesi[sz]er|scout notes?|frontier (model|estimate)|(?<!\bbank(?: of [a-z]+)?(?:'s)? )base rate|reference class|calibrat|no traded market price provided)/i;

/** The uncertainties to show a reader: about the world or the evidence, never the forecasting. */
export function worldUncertainties(list) {
  return strings(list).filter((u) => !ABOUT_THE_FORECASTING.test(u));
}

/** "prediction-market-implied" → "Prediction market implied". */
export function methodTagLabel(tag) {
  const words = tag.replace(/[-_]+/g, " ").trim();
  return words ? words[0].toUpperCase() + words.slice(1) : "";
}

/** The frontier estimate the forecaster was given: P(YES), or ranked outcomes, plus its reasoning. */
function frontierEstimateOf(raw) {
  if (!raw || typeof raw !== "object") return null;
  const p = number(raw.p);
  const outcomes =
    raw.pmf && typeof raw.pmf === "object"
      ? Object.entries(raw.pmf)
          .filter(([, v]) => number(v) != null)
          .map(([label, v]) => ({ label, p: v }))
          .sort((a, b) => b.p - a.p)
      : [];
  if (p == null && !outcomes.length) return null;
  return { p, outcomes, rationale: text(raw.rationale) };
}

/**
 * The report's analysis block, normalized; null when there is nothing to show.
 * @param {object|null} report the report object (`r`)
 */
export function reportAnalysisOf(report) {
  const a = report?.analysis && typeof report.analysis === "object" ? report.analysis : {};
  const quantiles = a.quantiles && typeof a.quantiles === "object" ? a.quantiles : {};
  const out = {
    summary: RECEIPT_ONLY.test(text(a.summary))
      ? ""
      : text(a.summary).replace(SUBMISSION_RECEIPT, ""),
    criteriaRead: text(a.criteria_read),
    referenceClass: text(a.reference_class),
    baseRate: number(a.base_rate),
    methodTags: strings(a.method_tags).map(methodTagLabel).filter(Boolean),
    keyUncertainties: strings(a.key_uncertainties).length
      ? strings(a.key_uncertainties)
      : strings(report?.summaries),
    // The server's rewrite when present; otherwise the source list, filtered.
    contentUncertainties: strings(a.content_uncertainties).length
      ? strings(a.content_uncertainties)
      : worldUncertainties(
          strings(a.key_uncertainties).length ? a.key_uncertainties : report?.summaries,
        ),
    quantiles: Object.entries(quantiles)
      .filter(([k, v]) => /^p\d{2}$/.test(k) && number(v) != null)
      .sort(([x], [y]) => Number(x.slice(1)) - Number(y.slice(1)))
      .map(([k, v]) => ({ percentile: Number(k.slice(1)), value: v })),
    frontierEstimate: frontierEstimateOf(a.frontier_estimate),
    scoutNotes: strings(a.scout_notes),
  };
  const empty =
    !out.summary &&
    !out.criteriaRead &&
    !out.referenceClass &&
    out.baseRate == null &&
    !out.methodTags.length &&
    !out.keyUncertainties.length &&
    !out.quantiles.length &&
    !out.frontierEstimate &&
    !out.scoutNotes.length;
  return empty ? null : out;
}

const pct = (p) => `${fmtPct(p)}%`;

/**
 * The leading outcome of a distribution, as "label 62%"; "" when there is none. Led by the
 * shared rule, so `otherLabel` (the catch-all's key) leads only past half the mass.
 */
function leading(pmf, buckets = null) {
  if (!pmf || typeof pmf !== "object") return "";
  const b = Array.isArray(buckets) ? buckets : [];
  const otherLabel = b.find((x) => x?.isOther === true)?.label ?? null;
  // `pmfMap` pairs an array PMF with its buckets by slot; an object PMF is keyed by label.
  const entries = [...pmfMap(pmf, b)];
  const rows = entries
    .filter(([, v]) => number(v) != null)
    .map(([label, p]) => ({ label, p, ...(label === otherLabel ? { isOther: true } : {}) }));
  const top = leadingRow(rows);
  return top ? `${bucketLabel(top)} ${pct(top.p)}` : "";
}

/**
 * When a trigger is decided: its window's end (`date_end`) when it has one — the deadline it
 * resolves by — else its single `date`. `date` alone is the window's START, which for every
 * trigger of a fresh forecast is the forecast date itself.
 * @param {object} ev one report event
 * @returns {{date: string, by: boolean}} the date to show, and whether it is a deadline
 */
export function triggerWhenOf(ev) {
  const end = text(ev?.date_end);
  const start = text(ev?.date);
  if (end && end !== start) return { date: end, by: true };
  return { date: start || end, by: false };
}

/**
 * What a trigger does to the answer and how to check it. `ifItHappens` / `ifNot` are the
 * answer's probability (binary) or most likely outcome (multi-value) in each case.
 * @param {object} ev one report event
 * @param {Array<{label: string, isOther?: boolean}>|null} [buckets] the current distribution,
 *   to tell the catch-all apart from a named outcome
 */
export function triggerDetailOf(ev, buckets = null) {
  const binary = number(ev?.a) != null || number(ev?.b) != null;
  return {
    ifItHappens: binary ? (number(ev.a) != null ? pct(ev.a) : "") : leading(ev?.a_pmf, buckets),
    ifNot: binary ? (number(ev.b) != null ? pct(ev.b) : "") : leading(ev?.b_pmf, buckets),
    howToCheck: text(ev?.resolution_criteria),
    source: text(ev?.source),
  };
}

/** A pmf as label → probability: the wire's object, or an array in the buckets' order. */
function pmfMap(pmf, buckets) {
  if (Array.isArray(pmf)) {
    // An array PMF is in outcome-SLOT order, while `buckets` may be display-sorted: pair each
    // entry with the bucket carrying that slot, falling back to position for foreign rows.
    const bySlot = new Map(
      buckets.map((b, i) => [Number.isInteger(b.outcomeIndex) ? b.outcomeIndex : i, b]),
    );
    return new Map(
      pmf.flatMap((p, i) => (bySlot.get(i) && number(p) != null ? [[bySlot.get(i).label, p]] : [])),
    );
  }
  if (pmf && typeof pmf === "object") {
    const entries = Object.entries(pmf).filter(([, p]) => number(p) != null);
    return new Map(entries.map(([k, p]) => [intervalDisplayLabel(k, buckets), p]));
  }
  return new Map();
}

/** The leader of `rows` on `key`, by the shared rule (the catch-all only past half the mass). */
const leaderOf = (rows, key) => leadingRow(rows.map((r) => ({ ...r, p: r[key] })));

/**
 * How a multi-outcome trigger moves each outcome: where it stands now, if the trigger
 * happens and if it doesn't. Keeps the outcomes it moves most (by the gap between the two
 * cases), dropping any it moves by less than `minSwing`. "Now" is the current forecast's
 * probability, or — where the forecast is absent — `p·ifYes + (1−p)·ifNo`, which the engine
 * holds equal to it. Notes when either case changes which outcome leads.
 *
 * @param {object} ev one report event carrying `a_pmf` / `b_pmf`
 * @param {Array<{label: string, p: number}>|null} buckets the current forecast distribution
 * @param {{max?: number, minSwing?: number}} [opts]
 * @returns {null | {
 *   rows: Array<{label: string, now: number, ifYes: number, ifNo: number, swing: number}>,
 *   leadIfYes: string|null,   // the outcome that takes the lead if it happens, when that changes it
 *   leadIfNo: string|null,
 * }}
 */
export function triggerOutcomeShiftsOf(ev, buckets, { max = 3, minSwing = 0.02 } = {}) {
  const b = Array.isArray(buckets) ? buckets.filter((x) => x && number(x.p) != null) : [];
  const yes = pmfMap(ev?.a_pmf, b);
  const no = pmfMap(ev?.b_pmf, b);
  if (!yes.size || !no.size) return null;
  const nowOf = new Map(b.map((x) => [x.label, x.p]));
  const otherLabel = b.find((x) => x.isOther === true)?.label ?? null;
  const pEvt = number(ev.p_event ?? ev.probability);
  const all = [...yes.keys()]
    .filter((label) => no.has(label))
    .map((label) => {
      const ifYes = yes.get(label);
      const ifNo = no.get(label);
      const now = nowOf.get(label) ?? (pEvt != null ? pEvt * ifYes + (1 - pEvt) * ifNo : null);
      return {
        label,
        ...(label === otherLabel ? { isOther: true } : {}),
        now,
        ifYes,
        ifNo,
        swing: Math.abs(ifYes - ifNo),
      };
    })
    .filter((r) => r.now != null);
  if (!all.length) return null;
  const lead = leaderOf(all, "now");
  const yesLead = leaderOf(all, "ifYes");
  const noLead = leaderOf(all, "ifNo");
  // Named for display: the catch-all reads as a group.
  const changedTo = (x) => (x && x.label !== lead?.label ? bucketLabel(x) : null);
  const rows = all
    .filter((r) => r.swing >= minSwing)
    .sort((x, y) => y.swing - x.swing)
    .slice(0, max);
  return {
    rows,
    leadIfYes: changedTo(yesLead),
    leadIfNo: changedTo(noLead),
  };
}

/**
 * A trigger as two ticker lines — if it happens, if it doesn't — each listing the outcomes
 * that case moves, where they land and by how many points, biggest move first. A yes/no
 * question has one outcome, "Yes". A move under `minMove` is left out; `newLead` names the
 * outcome a case puts in front when it changes the leader.
 *
 * @param {object} ev one report event: `a`/`b` for a yes/no question, `a_pmf`/`b_pmf` else
 * @param {number|null} probability the current P(YES), for a yes/no question
 * @param {Array<{label: string, p: number}>|null} buckets the current distribution, else
 * @param {{max?: number, minMove?: number}} [opts]
 * @returns {null | Array<{key: "yes"|"no", moves: Array<{label: string, to: number, delta: number}>, newLead: string|null}>}
 */
export function triggerTickerOf(ev, probability, buckets, { max = 3, minMove = 0.01 } = {}) {
  const pEvt = number(ev?.p_event ?? ev?.probability);
  const a = number(ev?.a);
  const b = number(ev?.b);
  let rows;
  let lead = { yes: null, no: null };
  if (a != null && b != null) {
    const now = number(probability) ?? (pEvt != null ? pEvt * a + (1 - pEvt) * b : null);
    if (now == null) return null;
    rows = [{ label: "Yes", now, ifYes: a, ifNo: b }];
  } else {
    const shifts = triggerOutcomeShiftsOf(ev, buckets, { max: Infinity, minSwing: 0 });
    if (!shifts) return null;
    rows = shifts.rows;
    lead = { yes: shifts.leadIfYes, no: shifts.leadIfNo };
  }
  return ["yes", "no"].map((key) => ({
    key,
    moves: rows
      .map((r) => {
        const to = key === "yes" ? r.ifYes : r.ifNo;
        return { label: r.label, ...(r.isOther ? { isOther: true } : {}), to, delta: to - r.now };
      })
      .filter((m) => Math.abs(m.delta) >= minMove)
      .sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta))
      .slice(0, max),
    newLead: lead[key],
  }));
}
